import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form } from "react-bootstrap";
import {
  fetchUsers,
  fetchUserSubscriptions,
  fetchSubscriptions,
  subscribeUser,
  deleteUser,
  deactivateUserSubscription,
} from "../../utilities/AdminUtilities";
// Component: Modal for Viewing User Info and Managing Subscriptions
import { Spinner, Badge } from "react-bootstrap";
import NewUserModal from "./NewUserModal";
import { Field, initials } from "./AdminUi";

// ---------------------------------------------
// Component: Search toolbar
const SearchToolbar = ({
  searchFirstName,
  setSearchFirstName,
  searchLastName,
  setSearchLastName,
  searchEmail,
  setSearchEmail,
  partnerUsersOnly,
  setPartnerUsersOnly,
  onAdd,
}) => {
  const hasFilters = searchFirstName || searchLastName || searchEmail;
  return (
    <div className="up-toolbar">
      <div className="up-filters">
        <Form.Control
          value={searchFirstName}
          onChange={(e) => setSearchFirstName(e.target.value)}
          placeholder="First name"
          aria-label="Filter by first name"
        />
        <Form.Control
          value={searchLastName}
          onChange={(e) => setSearchLastName(e.target.value)}
          placeholder="Last name"
          aria-label="Filter by last name"
        />
        <Form.Control
          className="up-filter-wide"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          placeholder="Email"
          aria-label="Filter by email"
        />
        {hasFilters && (
          <Button
            variant="link"
            className="up-clear"
            onClick={() => {
              setSearchFirstName("");
              setSearchLastName("");
              setSearchEmail("");
            }}
          >
            Clear
          </Button>
        )}
        <Form.Check
          type="switch"
          id="partner-users-only"
          className="text-nowrap"
          label="Partner users only"
          title="Show only users created by partner embeds (e.g. HERE), one per partner end user"
          checked={partnerUsersOnly}
          onChange={(e) => setPartnerUsersOnly(e.target.checked)}
        />
      </div>
      <Button variant="primary" onClick={onAdd}>
        + Add New User
      </Button>
    </div>
  );
};

const handleUnsubscribe = async (sub, user) => {
  try {
    const response = await deactivateUserSubscription({
      userId: user.id,
      subscriptionId: sub.subscription_id,
    });

    if (response.success) {
      alert(`Unsubscribed ${user.first_name} from ${sub.subscription_name}`);
      // Optionally call a refetch method
      // e.g., refreshSubscriptions(user.id);
    } else {
      alert(
        `Failed to unsubscribe: ${
          response.errorObject?.message || "Unknown error"
        }`
      );
    }
  } catch (error) {
    alert("Error unsubscribing user.");
    console.error("Unsubscribe error:", error);
  }
};
const formatDate = (isoString) => {
  if (!isoString) return " - ";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
// ---------------------------------------------
// Component: User Row
const UserRow = ({ user, onClick }) => (
  <tr className="up-row" onClick={() => onClick(user)}>
    <td>
      <div className="up-person">
        <span className="up-avatar">{initials(user)}</span>
        <span className="up-name">{user.first_name || "—"}</span>
      </div>
    </td>
    <td>{user.last_name || "—"}</td>
    <td className="up-email">{user.email || "—"}</td>
    <td>{user.organization?.organisation_name || "—"}</td>
    <td>
      {user.role ? (
        <Badge bg="secondary" pill>
          {user.role}
        </Badge>
      ) : (
        "—"
      )}
    </td>
    <td className="up-date">{user.last_accessed_at ? formatDate(user.last_accessed_at) : "—"}</td>
  </tr>
);

// ---------------------------------------------

const SubscriptionTable = ({ rows, empty, renderAction }) =>
  rows.length === 0 ? (
    <div className="up-empty">{empty}</div>
  ) : (
    <Table responsive size="sm" className="up-subtable">
      <thead>
        <tr>
          <th>Name</th>
          <th>Details</th>
          <th>Validity</th>
          <th className="text-end">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((sub) => (
          <tr key={sub.id}>
            <td className="fw-semibold">{sub.subscription_name}</td>
            <td>{sub.details || "—"}</td>
            <td>
              {sub.validity === -1 ? "Unlimited" : `${sub.validity} days`}
            </td>
            <td className="text-end">{renderAction(sub)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

const UserInfoModal = ({
  show,
  onHide,
  selectedUser,
  subscriptions,
  subscribedIds,
  onSubscribe,
  onUnsubscribe,
  onDeleteUser,
  loadingSubscriptions,
}) => {
  const [subView, setSubView] = useState("active");

  if (!selectedUser) return null;

  // Extract actual subscription IDs from user's current subscriptions
  const unsubscribed = subscriptions.filter(
    (sub) =>
      !subscribedIds.some((userSub) => userSub.subscription_id === sub.id)
  );

  const fullName =
    [selectedUser.first_name, selectedUser.last_name]
      .filter(Boolean)
      .join(" ") || "User";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable dialogClassName="up-dialog">
      <Modal.Header closeButton className="pv-header">
        <div className="up-modal-head">
          <span className="up-avatar up-avatar-lg">{initials(selectedUser)}</span>
          <div className="min-w-0">
            <div className="pv-eyebrow">User</div>
            <div className="pv-title up-title text-break">{fullName}</div>
            <div className="up-modal-sub text-break">
              {selectedUser.email || "—"}
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="pv-section-title">Profile</div>
        <div className="up-profile">
          <Field label="Phone" value={selectedUser.phone_number} />
          <Field label="Role">
            {selectedUser.role ? (
              <Badge bg="secondary" pill>
                {selectedUser.role}
              </Badge>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Status">
            <Badge bg={selectedUser.is_active ? "success" : "danger"} pill>
              {selectedUser.is_active ? "Active" : "Inactive"}
            </Badge>
          </Field>
          <Field
            label="Organization"
            value={selectedUser?.organization?.organisation_name}
          />
          <Field label="Account" value={selectedUser?.account?.name} />
          <Field
            label="Last Login"
            value={
              selectedUser.last_accessed_at
                ? formatDate(selectedUser.last_accessed_at)
                : null
            }
          />
        </div>

        <div className="up-subhead">
          <div className="pv-section-title mb-0">Subscriptions</div>
          <div className="up-segment" role="tablist">
            <button
              type="button"
              className={subView === "active" ? "is-on" : ""}
              onClick={() => setSubView("active")}
            >
              Active ({subscribedIds.length})
            </button>
            <button
              type="button"
              className={subView === "available" ? "is-on" : ""}
              onClick={() => setSubView("available")}
            >
              Available ({unsubscribed.length})
            </button>
          </div>
        </div>

        {subView === "active" ? (
          loadingSubscriptions ? (
            <div className="text-center my-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <SubscriptionTable
              rows={subscribedIds}
              empty="No active subscriptions."
              renderAction={(sub) =>
                sub.reference_type === "USER_ID" ? (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => onUnsubscribe(sub, selectedUser)}
                  >
                    Remove
                  </Button>
                ) : (
                  <span className="text-muted small">
                    Managed through account
                  </span>
                )
              }
            />
          )
        ) : (
          <SubscriptionTable
            rows={unsubscribed}
            empty="No new subscriptions available."
            renderAction={(sub) => (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => onSubscribe(sub, selectedUser)}
              >
                Subscribe
              </Button>
            )}
          />
        )}
      </Modal.Body>

      <Modal.Footer className="ep-footer">
        <Button variant="light" onClick={onDeleteUser} className="me-auto up-delete">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v5M14 11v5" />
          </svg>
          Delete User
        </Button>
        <Button variant="outline-secondary" onClick={onHide} className="up-close">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------
// Main Component
function UserDetails() {
  const [users, setUsers] = useState([]);
  const [limit] = useState(20);
  const [offset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchPhoneNumber, setSearchPhoneNumber] = useState("");
  const [userSubscriptionIds, setUserSubscriptionIds] = useState([]);
  const [orderBy, setOrderBy] = useState("last_accessed_at");
  const [orderByDirection, setOrderByDirection] = useState("desc");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [partnerUsersOnly, setPartnerUsersOnly] = useState(false);

  useEffect(() => {
    fetchSubscriptions(setSubscriptions);
  }, []);

  useEffect(() => {
    fetchUsers(
      limit,
      offset,
      searchEmail,
      searchFirstName,
      searchLastName,
      searchPhoneNumber,
      setUsers,
      currentPage,
      orderBy,
      orderByDirection,
      partnerUsersOnly
    );
  }, [
    searchEmail,
    searchFirstName,
    searchLastName,
    searchPhoneNumber,
    currentPage,
    orderBy,
    orderByDirection,
    partnerUsersOnly,
  ]);

  const handleRowClick = (user) => {
    setSelectedUser(user);
    fetchUserSubscriptions(user.id, setUserSubscriptionIds);
    setShowUserModal(true);
  };

  const handleSubscribe = async (sub, user) => {
    const ok = await subscribeUser(sub, user, setShowUserModal);
    if (ok) fetchUserSubscriptions(user.id, setUserSubscriptionIds);
  };

  const handleDeactivateUser = async () => {
    try {
      await deleteUser({ id: selectedUser.id });
      alert(`User ${selectedUser?.first_name} deactivated.`);
      setShowUserModal(false);
      fetchUsers(
        limit,
        offset,
        searchEmail,
        searchFirstName,
        searchLastName,
        searchPhoneNumber,
        setUsers,
        currentPage,
        orderBy,
        orderByDirection,
        partnerUsersOnly
      );
    } catch (error) {
      alert("Failed to deactivate user.");
      console.error("Error:", error);
    }
  };

  return (
    <>
      <SearchToolbar
        searchFirstName={searchFirstName}
        setSearchFirstName={setSearchFirstName}
        searchLastName={searchLastName}
        setSearchLastName={setSearchLastName}
        searchEmail={searchEmail}
        setSearchEmail={setSearchEmail}
        partnerUsersOnly={partnerUsersOnly}
        setPartnerUsersOnly={(value) => {
          setPartnerUsersOnly(value);
          setCurrentPage(1);
        }}
        onAdd={() => setShowCreateUser(true)}
      />

      <div className="up-card">
        <Table hover responsive className="up-table">
          <thead>
            <tr>
              <SortableHeader
                label="First Name"
                field="first_name"
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                setOrderBy={setOrderBy}
                setOrderByDirection={setOrderByDirection}
              />
              <SortableHeader
                label="Last Name"
                field="last_name"
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                setOrderBy={setOrderBy}
                setOrderByDirection={setOrderByDirection}
              />
              <SortableHeader
                label="Email"
                field="email"
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                setOrderBy={setOrderBy}
                setOrderByDirection={setOrderByDirection}
              />
              <th>Organization</th>
              <th>Role</th>
              <SortableHeader
                label="Last Login (UTC)"
                field="last_accessed_at"
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                setOrderBy={setOrderBy}
                setOrderByDirection={setOrderByDirection}
              />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="up-empty">
                  No users match your filters.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <UserRow key={user.id} user={user} onClick={handleRowClick} />
              ))
            )}
          </tbody>
        </Table>
      </div>

      <div className="up-pager">
        <span className="up-pager-info">Page {currentPage}</span>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={users.length < limit}
          >
            Next →
          </Button>
        </div>
      </div>

      <UserInfoModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        selectedUser={selectedUser}
        subscriptions={subscriptions}
        subscribedIds={userSubscriptionIds}
        onSubscribe={handleSubscribe}
        onDeleteUser={handleDeactivateUser}
        onUnsubscribe={handleUnsubscribe}
      />
      <NewUserModal
  show={showCreateUser}
  onHide={() => setShowCreateUser(false)}
  onSuccess={() => {
    // refresh the list exactly like you already do
    fetchUsers(
      limit,
      offset,
      searchEmail,
      searchFirstName,
      searchLastName,
      searchPhoneNumber,
      setUsers,
      currentPage,
      orderBy,
      orderByDirection,
      partnerUsersOnly
    );
  }}
/>
    </>
  );
}

export default UserDetails;
const SortableHeader = ({
  label,
  field,
  orderBy,
  orderByDirection,
  setOrderBy,
  setOrderByDirection,
}) => {
  const handleClick = () => {
    if (orderBy === field) {
      // toggle direction
      setOrderByDirection(orderByDirection === "asc" ? "desc" : "asc");
    } else {
      // switch field and reset direction
      setOrderBy(field);
      setOrderByDirection("asc");
    }
  };

  const renderArrow = () => {
    if (orderBy !== field) return "↕";
    return orderByDirection === "asc" ? "↑" : "↓";
  };

  return (
    <th
      className={`up-sortable${orderBy === field ? " is-sorted" : ""}`}
      onClick={handleClick}
    >
      {label} <span className="up-sort-arrow">{renderArrow()}</span>
    </th>
  );
};
